import { useEffect, useMemo, useState } from "react";
import Layout, { PageHeader, Card } from "../components/Layout";
import { api } from "../lib/api";
import { EVENT_DATE, TOTAL_INVENTORY_CR, SPONSOR_RACK_CR, ACTIVATION_RACK_CR, WAVES, SPONSOR_TIERS, ACTIVATION_TIERS, FLEA_TOTAL_SLOTS, FLEA_RACK_LAKHS, FLEA_BASE_LAKHS, effectiveValue } from "../lib/constants";
import { formatINR, formatLakhs, formatNumber, formatPercent } from "../lib/format";
import { AlertTriangle, TrendingUp, Calendar, Users, IndianRupee, Store, CalendarClock } from "lucide-react";
import WeatherWidget from "../components/WeatherWidget";

function useCountdown(target) {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);
  const diff = Math.max(0, target.getTime() - now.getTime());
  const days = Math.floor(diff / 86400000);
  const hours = Math.floor((diff % 86400000) / 3600000);
  const mins = Math.floor((diff % 3600000) / 60000);
  const secs = Math.floor((diff % 60000) / 1000);
  return { days, hours, mins, secs };
}

function KpiCard({ overline, label, value, sub, progress, testId, accent = "emerald" }) {
  const barCls = accent === "orange" ? "bg-orange-500" : "bg-emerald-500";
  return (
    <Card className="p-6 relative" testId={testId}>
      <div className="overline mb-3">{overline}</div>
      <div className="font-display text-4xl md:text-[40px] font-bold tracking-tighter tabular leading-none">
        {value}
      </div>
      <div className="mt-2 text-sm text-zinc-400">{label}</div>
      {progress != null && (
        <>
          <div className="mt-5 h-1 bg-white/5 overflow-hidden rounded-full">
            <div
              className={`h-full ${barCls} transition-all rounded-full`}
              style={{ width: `${Math.min(100, progress)}%` }}
            />
          </div>
          <div className="mt-2 text-xs text-zinc-500 tabular">{sub}</div>
        </>
      )}
      {progress == null && sub && (
        <div className="mt-4 text-xs text-zinc-500 tabular">{sub}</div>
      )}
    </Card>
  );
}

export default function Overview() {
  const { days, hours, mins, secs } = useCountdown(EVENT_DATE);
  const [sponsorships, setSponsorships] = useState([]);
  const [backstage, setBackstage] = useState([]);
  const [flea, setFlea] = useState([]);
  const [tickets, setTickets] = useState(null);
  const [ig, setIg] = useState([]);
  const [calendarEvents, setCalendarEvents] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [s, b, f, t, i, c] = await Promise.all([
          api.get("/sponsorships"),
          api.get("/backstage"),
          api.get("/flea-market"),
          api.get("/tickets/latest"),
          api.get("/instagram"),
          api.get("/calendar/upcoming?limit=5"),
        ]);
        setSponsorships(s.data);
        setBackstage(b.data);
        setFlea(f.data);
        setTickets(t.data && t.data.id ? t.data : null);
        setIg(i.data);
        setCalendarEvents(c.data.events || []);
      } catch (e) { /* protected route handles auth */ }
    })();
  }, []);

  const sponsorClosedCr = useMemo(
    () => sponsorships.filter((r) => r.kind === "sponsor" && r.status === "closed")
                      .reduce((s, r) => s + effectiveValue(r), 0),
    [sponsorships]
  );
  const activationClosedCr = useMemo(
    () => sponsorships.filter((r) => r.kind === "activation" && r.status === "closed")
                      .reduce((s, r) => s + effectiveValue(r), 0),
    [sponsorships]
  );
  const totalClosed = sponsorClosedCr + activationClosedCr;

  const backstageSoldLakhs = useMemo(
    () => backstage.filter((b) => b.status === "sold").reduce((s, b) => s + b.price_lakhs, 0),
    [backstage]
  );

  const fleaClosed = useMemo(
    () => flea.filter((r) => r.status === "closed"),
    [flea]
  );
  const fleaFilledSlots = fleaClosed.reduce((s, r) => s + r.slots_taken, 0);
  const fleaRevenueLakhs = fleaClosed.reduce((s, r) => {
    const cp = r.closing_price_lakhs;
    if (cp != null && cp !== "" && !Number.isNaN(Number(cp))) return s + Number(cp);
    return s + r.slots_taken * FLEA_BASE_LAKHS;
  }, 0);

  const ticketKpi = tickets || { total_sold: 0, total_capacity: 0, gross_revenue: 0 };
  const latestIg = ig[0];

  const wave1Hit = sponsorClosedCr >= 3.0;

  return (
    <Layout>
      <PageHeader
        overline="Mission control"
        title="ISF 2026 · Delhi"
        subtitle="Internal command deck for the Indian Sneaker Festival. All numbers update in real-time."
        actions={
          <div className="flex items-center gap-3">
            <WeatherWidget />
            <div className="flex items-center gap-2 text-xs text-zinc-300 border border-white/10 bg-white/[0.03] backdrop-blur-md px-3 py-2 rounded-xl">
              <span className="dot-live" />
              <span className="uppercase tracking-[0.18em]">Live</span>
            </div>
          </div>
        }
        testId="overview-header"
      />

      {/* Countdown */}
      <Card className="mb-8 p-8 grid-bg" testId="countdown-card">
        <div className="flex items-end justify-between flex-wrap gap-6">
          <div>
            <div className="overline mb-3 flex items-center gap-2">
              <Calendar className="h-3.5 w-3.5" /> Countdown · 19 Dec 2026
            </div>
            <div className="grid grid-cols-4 gap-6 md:gap-10 tabular">
              {[
                { v: days, l: "Days" },
                { v: hours, l: "Hours" },
                { v: mins, l: "Minutes" },
                { v: secs, l: "Seconds" },
              ].map((x) => (
                <div key={x.l}>
                  <div className="font-display font-bold text-5xl md:text-6xl text-emerald-500 leading-none">
                    {String(x.v).padStart(2, "0")}
                  </div>
                  <div className="mt-2 overline">{x.l}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="text-right">
            <div className="overline mb-2">Venue</div>
            <div className="font-display text-xl">Gurugram</div>
            <div className="text-xs text-zinc-500 mt-1">19 – 20 December 2026</div>
          </div>
        </div>
      </Card>

      {!wave1Hit && (
        <div
          data-testid="wave1-alert"
          className="mb-6 border border-amber-500/30 bg-amber-500/10 px-5 py-4 flex items-start gap-3"
        >
          <AlertTriangle className="h-5 w-5 text-amber-400 mt-0.5" />
          <div>
            <div className="font-medium text-amber-200">Wave 1 target not yet hit</div>
            <div className="text-sm text-amber-100/70 mt-0.5">
              Sponsor revenue closed is {formatINR(sponsorClosedCr)} — Wave 1 target is ₹ 3 Cr by end of August.
            </div>
          </div>
        </div>
      )}

      {/* KPI grid — 5 cards, sponsors and activations shown distinctly */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
        <KpiCard
          overline="Total closed"
          label={`of ${formatINR(TOTAL_INVENTORY_CR)} combined inventory`}
          value={formatINR(totalClosed)}
          progress={(totalClosed / TOTAL_INVENTORY_CR) * 100}
          sub={`${formatPercent(totalClosed, TOTAL_INVENTORY_CR)} of rack value secured`}
          testId="kpi-total"
        />
        <KpiCard
          overline="Sponsors"
          label={`of ${formatINR(SPONSOR_RACK_CR)} sponsor rack`}
          value={formatINR(sponsorClosedCr)}
          progress={(sponsorClosedCr / SPONSOR_RACK_CR) * 100}
          sub={`${formatPercent(sponsorClosedCr, SPONSOR_RACK_CR)} closed`}
          testId="kpi-sponsors"
        />
        <KpiCard
          overline="Activations"
          label={`of ${formatINR(ACTIVATION_RACK_CR)} activation rack`}
          value={formatINR(activationClosedCr)}
          progress={(activationClosedCr / ACTIVATION_RACK_CR) * 100}
          sub={`${formatPercent(activationClosedCr, ACTIVATION_RACK_CR)} closed`}
          testId="kpi-activations"
        />
        <KpiCard
          overline="Ticket sales"
          label={tickets ? "Latest BookMyShow snapshot" : "No snapshot yet"}
          value={formatNumber(ticketKpi.total_sold)}
          progress={ticketKpi.total_capacity ? (ticketKpi.total_sold / ticketKpi.total_capacity) * 100 : 0}
          sub={ticketKpi.total_capacity
              ? `${formatNumber(ticketKpi.total_sold)} of ${formatNumber(ticketKpi.total_capacity)} sold`
              : "Upload BMS xlsx to populate"}
          testId="kpi-tickets"
          accent="orange"
        />
        <KpiCard
          overline="Instagram"
          label={latestIg ? "Latest manual snapshot" : "No snapshot yet"}
          value={latestIg ? formatNumber(latestIg.followers) : "—"}
          sub={latestIg ? `${formatNumber(latestIg.posts)} posts · ${(latestIg.avg_engagement || 0).toFixed(1)}% engagement` : "Log a snapshot to begin tracking"}
          testId="kpi-instagram"
        />
      </div>

      {/* Tier breakdown — sponsor vs activation, side by side */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
        <Card className="p-6" testId="card-sponsor-tiers">
          <div className="flex items-end justify-between mb-5">
            <div>
              <div className="overline mb-2">Sponsor tier inventory</div>
              <div className="font-display text-lg">Slots filled vs available</div>
            </div>
            <div className="text-right">
              <div className="overline mb-1">Closed</div>
              <div className="font-display text-lg tabular text-emerald-400">{formatINR(sponsorClosedCr)}</div>
            </div>
          </div>
          <div className="space-y-3">
            {SPONSOR_TIERS.map((t) => {
              const closedRows = sponsorships.filter((r) => r.tier === t.id && r.status === "closed");
              const filled = closedRows.reduce((s, r) => s + r.slots_taken, 0);
              const revenue = closedRows.reduce((s, r) => s + effectiveValue(r), 0);
              const pct = (filled / t.slots) * 100;
              return (
                <div key={t.id}>
                  <div className="flex items-baseline justify-between text-xs mb-1">
                    <span className="text-zinc-300">{t.label}</span>
                    <span className="tabular text-zinc-500">
                      <span className="text-white">{filled}</span>/{t.slots} · <span className="text-emerald-400">{formatINR(revenue)}</span>
                    </span>
                  </div>
                  <div className="h-[3px] bg-zinc-900">
                    <div className="h-full bg-emerald-500 transition-all" style={{ width: `${Math.min(100, pct)}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </Card>

        <Card className="p-6" testId="card-activation-tiers">
          <div className="flex items-end justify-between mb-5">
            <div>
              <div className="overline mb-2">Activation tier inventory</div>
              <div className="font-display text-lg">Slots filled vs available</div>
            </div>
            <div className="text-right">
              <div className="overline mb-1">Closed</div>
              <div className="font-display text-lg tabular text-emerald-400">{formatINR(activationClosedCr)}</div>
            </div>
          </div>
          <div className="space-y-3">
            {ACTIVATION_TIERS.map((t) => {
              const closedRows = sponsorships.filter((r) => r.tier === t.id && r.status === "closed");
              const filled = closedRows.reduce((s, r) => s + r.slots_taken, 0);
              const revenue = closedRows.reduce((s, r) => s + effectiveValue(r), 0);
              const pct = (filled / t.slots) * 100;
              return (
                <div key={t.id}>
                  <div className="flex items-baseline justify-between text-xs mb-1">
                    <span className="text-zinc-300">{t.label}</span>
                    <span className="tabular text-zinc-500">
                      <span className="text-white">{filled}</span>/{t.slots} · <span className="text-emerald-400">{formatINR(revenue)}</span>
                    </span>
                  </div>
                  <div className="h-[3px] bg-zinc-900">
                    <div className="h-full bg-emerald-500 transition-all" style={{ width: `${Math.min(100, pct)}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      </div>

      {/* Secondary cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="p-6" testId="card-backstage">
          <div className="overline mb-3 flex items-center gap-2"><Users className="h-3.5 w-3.5" /> Backstage zones</div>
          <div className="font-display text-3xl font-bold tabular">
            {backstage.filter((b) => b.status === "sold").length}
            <span className="text-zinc-600 text-2xl"> / {backstage.length}</span>
          </div>
          <div className="mt-1 text-sm text-zinc-400">{formatINR(backstageSoldLakhs / 100)} locked</div>
        </Card>
        <Card className="p-6" testId="card-flea-market">
          <div className="overline mb-3 flex items-center gap-2"><Store className="h-3.5 w-3.5" /> Flea market</div>
          <div className="font-display text-3xl font-bold tabular">
            {fleaFilledSlots}<span className="text-zinc-600 text-2xl"> / {FLEA_TOTAL_SLOTS}</span>
          </div>
          <div className="mt-1 text-sm text-zinc-400">{formatLakhs(fleaRevenueLakhs)} closed / {formatLakhs(FLEA_RACK_LAKHS)} rack</div>
          <div className="mt-3 h-1 bg-zinc-900">
            <div className="h-full bg-emerald-500" style={{ width: `${Math.min(100, (fleaRevenueLakhs / FLEA_RACK_LAKHS) * 100)}%` }} />
          </div>
        </Card>
        <Card className="p-6" testId="card-wave-progress">
          <div className="overline mb-3 flex items-center gap-2"><TrendingUp className="h-3.5 w-3.5" /> Revenue by wave</div>
          <div className="space-y-2 mt-2">
            {WAVES.map((w) => {
              const revenue = sponsorships
                .filter((s) => s.status === "closed" && s.wave === w.id)
                .reduce((a, b) => a + effectiveValue(b), 0);
              return (
                <div key={w.id} className="flex items-center gap-3 text-sm">
                  <span className="w-16 text-zinc-500 text-xs">{w.label}</span>
                  <div className="flex-1 h-1 bg-zinc-900">
                    <div className="h-full bg-emerald-500" style={{ width: `${Math.min(100, (revenue / TOTAL_INVENTORY_CR) * 100)}%` }} />
                  </div>
                  <span className="tabular text-zinc-300 w-20 text-right">{formatINR(revenue)}</span>
                </div>
              );
            })}
          </div>
        </Card>
      </div>

      {/* Upcoming meetings */}
      <Card className="p-6 mt-8" testId="card-upcoming-meetings">
        <div className="flex items-end justify-between mb-5">
          <div>
            <div className="overline mb-2 flex items-center gap-2"><CalendarClock className="h-3.5 w-3.5" /> Upcoming meetings</div>
            <div className="font-display text-lg">Next on your calendar</div>
          </div>
          <a href="/calendar" className="text-xs text-emerald-400 hover:text-emerald-300">View all →</a>
        </div>
        {calendarEvents.length === 0 ? (
          <div className="py-6 text-center text-sm text-zinc-500">Nothing on the calendar in the next 60 days.</div>
        ) : (
          <div className="divide-y divide-zinc-900">
            {calendarEvents.map((e) => {
              const d = new Date(e.start);
              const dayLabel = d.toLocaleDateString("en-IN", { weekday: "short", day: "2-digit", month: "short" });
              const timeLabel = e.all_day ? "All day" : d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: false });
              return (
                <div key={e.id} className="flex items-start gap-4 py-3" data-testid="overview-event">
                  <div className="w-24 shrink-0">
                    <div className="text-xs text-zinc-400 tabular">{dayLabel}</div>
                    <div className="font-display text-sm text-emerald-400 tabular">{timeLabel}</div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm truncate">{e.title}</div>
                    {e.location && <div className="text-xs text-zinc-500 truncate">{e.location}</div>}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>
    </Layout>
  );
}
